import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Edit2, Shield, Plus, Trash2 } from "lucide-react";
import api from "../lib/api";

export default function Settings() {
  const { user } = useAuth();
  
  // General State
  const [municipality, setMunicipality] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Desk State
  const [desks, setDesks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [isDeskModalOpen, setIsDeskModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    rut: "",
    logo_url: ""
  });
  
  const [deskData, setDeskData] = useState({
    name: "",
    assigned_worker_id: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (!user.municipality_id) {
        toast.error("Error: Usuario sin municipalidad asignada");
        return;
      }

      // Fetch Municipality
      const muniRes = await api.get(`/municipalities/${user.municipality_id}`);
      setMunicipality(muniRes.data);
      const conf = muniRes.data.config || {};
      setFormData({
        name: conf.name || "",
        rut: conf.rut || "",
        logo_url: conf.logo_url || ""
      });

      // Fetch Desks
      const desksRes = await api.get("/desks/");
      setDesks(desksRes.data);

      // Fetch Workers for assigning to desks
      const usersRes = await api.get("/users/");
      const sw = usersRes.data.filter(u => u.role === 'social_worker');
      setWorkers(sw);

    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  }, [user.municipality_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateGeneral = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/municipalities/${user.municipality_id}`, formData);
      toast.success("Configuración actualizada", {
        description: "Los datos de la municipalidad se guardaron correctamente."
      });
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar la configuración");
    }
  };

  const handleCreateDesk = async (e) => {
    e.preventDefault();
    try {
      await api.post("/desks/", deskData);
      toast.success("Caja PAC creada exitosamente");
      setIsDeskModalOpen(false);
      setDeskData({ name: "", assigned_worker_id: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al crear la caja");
    }
  };
  
  const handleDeleteDesk = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta Caja PAC?")) return;
    try {
      await api.delete(`/desks/${id}`);
      toast.success("Caja eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar la caja");
    }
  };

  const getWorkerName = (id) => {
    const worker = workers.find(w => w.user_id === id);
    return worker ? worker.name : "Sin asignar";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Configuración del Sistema</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestiona la información de la municipalidad y administra las Cajas PAC.
          </p>
        </div>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="desks">Cajas PAC</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Información Municipal</CardTitle>
              <CardDescription>Esta información se mostrará en los reportes y documentos generados por el sistema.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateGeneral} className="space-y-4 max-w-xl">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la Municipalidad</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="rut">RUT</Label>
                  <Input
                    id="rut"
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="logo_url">URL del Logo</Label>
                  <Input
                    id="logo_url"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://ejemplo.com/logo.png"
                  />
                  {formData.logo_url && (
                    <div className="mt-2 text-sm text-slate-500">
                      Vista previa:
                      <img src={formData.logo_url} alt="Logo preview" className="h-16 mt-1 object-contain" onError={(e) => e.target.style.display='none'} />
                    </div>
                  )}
                </div>

                <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                  Guardar Cambios
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="desks">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Cajas PAC</CardTitle>
                <CardDescription>
                  Administra las cajas de atención e historias de derivación.
                </CardDescription>
              </div>
              <Button onClick={() => setIsDeskModalOpen(true)} className="bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Caja PAC
              </Button>
            </CardHeader>
            <CardContent>
              {desks.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-slate-900">No hay Cajas PAC</h3>
                  <p className="text-slate-500">Comienza creando una nueva caja de atención.</p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Trabajador Social</TableHead>
                        <TableHead>Estado Actual</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {desks.map((desk) => (
                        <TableRow key={desk._id}>
                          <TableCell className="font-medium">{desk.name}</TableCell>
                          <TableCell>
                            {desk.assigned_worker_id ? (
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-xs font-medium">
                                  {getWorkerName(desk.assigned_worker_id)[0].toUpperCase()}
                                </div>
                                <span>{getWorkerName(desk.assigned_worker_id)}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400 italic">Sin asignar</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {desk.is_open ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                Abierta
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                                Cerrada
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteDesk(desk._id)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Desk Modal */}
      <Dialog open={isDeskModalOpen} onOpenChange={setIsDeskModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Caja PAC</DialogTitle>
            <DialogDescription>
              Crea una nueva caja y asigna un trabajador social para que pueda atender beneficiarios.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateDesk} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="desk_name">Nombre de la Caja (Ej: PAC 1)</Label>
              <Input
                id="desk_name"
                value={deskData.name}
                onChange={(e) => setDeskData({ ...deskData, name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Trabajador Social Asignado</Label>
              <Select
                value={deskData.assigned_worker_id || "none"}
                onValueChange={(val) => setDeskData({ ...deskData, assigned_worker_id: val === "none" ? "" : val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar trabajador" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar por ahora</SelectItem>
                  {workers.map((w) => (
                    <SelectItem key={w.user_id} value={w.user_id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDeskModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                Crear Caja
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
